import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Loader2, Sparkles, RefreshCw, AlertTriangle } from "lucide-react";
import type { StudyLayer } from "@erc/shared";
import { customApi, type CustomListingDetail } from "../../lib/customListing";

// Custom Liste Studyosu 4. adim: ikon seti (menuden secilen "Design 1..N"). Konular ilham
// tablosundan okunur, ikonlar tek sayfada bizim stilimizle cizilir ve numaralara baglanir.
// Kartela, ornekler (secilen ikon referans) bu setten beslenir.

export function IconSetCard({ rec, layer, model, onChanged }: { rec: CustomListingDetail; layer: StudyLayer; model: string; onChanged: () => void }) {
  const set = rec.template.iconSet;
  const n = layer.options?.length ?? 0;
  const [text, setText] = useState((set?.subjects ?? []).join("\n"));
  const [styleNote, setStyleNote] = useState(set?.styleNote ?? "");
  const [err, setErr] = useState<string | null>(null);
  const [redo, setRedo] = useState<number | null>(null);
  const subjects = text.split("\n").map((s) => s.trim()).filter(Boolean);
  const running = set?.status === "running";
  const onError = (e: unknown) => setErr((e as Error).message);
  const read = useMutation({
    mutationFn: () => customApi<{ subjects: string[] }>("POST", `/${rec.id}/icon-set/subjects`),
    onSuccess: (d) => { setErr(null); setText(d.subjects.join("\n")); },
    onError,
  });
  const gen = useMutation({
    mutationFn: () => customApi("POST", `/${rec.id}/icon-set`, { subjects, model, styleNote }),
    onSuccess: () => { setErr(null); onChanged(); },
    onError,
  });
  const one = useMutation({
    mutationFn: (o: { i: number; subject?: string }) => customApi("POST", `/${rec.id}/icon-set/${o.i}`, { subject: o.subject }),
    onSuccess: () => { setErr(null); setRedo(null); onChanged(); },
    onError,
  });

  return (
    <div className="space-y-3 rounded-lg border border-gray-200 p-4">
      <div>
        <h3 className="text-sm font-semibold text-gray-900">İkon seti ({n} tasarım)</h3>
        <p className="mt-1 text-xs text-gray-600">
          Alıcı "{layer.labelTr}" menüsünden numara seçiyor. Konular ilham tablosundan okunur, {n} ikon tek sayfada bizim çizgi stilimizle çizilir (~$0.03, 1-2 dk; rakibin çizimleri kopyalanmaz).
          Kartela ve örnekler bu setten çıkar; "Design 7" seçen alıcıya 7 numaralı ikon basılır.
        </p>
      </div>

      <div className="grid gap-3 md:grid-cols-[1fr_16rem]">
        <div>
          <div className="mb-1 flex items-center justify-between">
            <label htmlFor="icon-subjects" className="text-xs font-medium text-gray-700">Konular (her satıra bir tane, sırayla) · {subjects.length}/{n}</label>
            <button onClick={() => read.mutate()} disabled={read.isPending} className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline disabled:opacity-50">
              {read.isPending && <Loader2 className="h-3 w-3 animate-spin" />} İlham tablosundan oku
            </button>
          </div>
          <textarea id="icon-subjects" value={text} onChange={(e) => setText(e.target.value)} rows={8} placeholder={"Santa Claus face\nsnowy cottage in woods\n…"}
            className="w-full rounded-lg border border-gray-200 px-2 py-1.5 font-mono text-xs focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200" />
        </div>
        <div className="space-y-2">
          <label htmlFor="icon-style" className="block text-xs font-medium text-gray-700">Stil notu (isteğe bağlı)</label>
          <input id="icon-style" value={styleNote} onChange={(e) => setStyleNote(e.target.value)} maxLength={200} placeholder="ör. thicker lines, retro vibe"
            className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200" />
          <button onClick={() => gen.mutate()} disabled={gen.isPending || running || subjects.length !== n}
            className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:bg-indigo-300">
            {gen.isPending || running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {running ? "İkonlar çiziliyor…" : set?.status === "ready" ? "Seti yeniden üret" : "İkon setini üret"}
          </button>
          {subjects.length !== n && <p className="text-xs text-amber-700">{n} konu gerekli</p>}
        </div>
      </div>

      {err && <p className="text-sm text-red-600">{err}</p>}
      {set?.status === "failed" && <p className="flex items-center gap-1 text-sm text-red-600"><AlertTriangle className="h-4 w-4" /> {set.error ?? "Üretilemedi."}</p>}
      {set?.status === "ready" && set.error && <p className="flex items-center gap-1 text-xs text-amber-700"><AlertTriangle className="h-3.5 w-3.5" /> {set.error}</p>}

      {set?.status === "ready" && (
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-5 lg:grid-cols-7">
          {set.icons.map((ic, i) => (
            <div key={i} className="rounded-lg border border-gray-200 bg-white p-1.5 text-center">
              {ic.url ? <img src={ic.url} alt={ic.subject} loading="lazy" className="mx-auto aspect-square w-full object-contain" /> : <div className="flex aspect-square items-center justify-center text-xs text-gray-400">Boş</div>}
              <p className="truncate text-xs font-medium text-gray-900">{ic.label}</p>
              <p className="truncate text-[11px] text-gray-500" title={ic.subject}>{ic.subject}</p>
              {redo === i ? (
                <form className="mt-1 flex gap-1" onSubmit={(e) => { e.preventDefault(); one.mutate({ i, subject: String(new FormData(e.currentTarget).get("s") ?? "") }); }}>
                  <input name="s" aria-label={`${ic.label} konusu`} defaultValue={ic.subject} maxLength={60} className="min-w-0 flex-1 rounded border border-gray-200 px-1 text-[11px]" />
                  <button type="submit" disabled={one.isPending} aria-label="Yeniden çiz" className="rounded bg-indigo-600 px-1.5 text-white disabled:opacity-50">
                    {one.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                  </button>
                </form>
              ) : (
                <button onClick={() => setRedo(i)} disabled={one.isPending} className="mt-1 text-[11px] text-indigo-600 hover:underline disabled:opacity-50">Yeniden üret</button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
